import { ICustomScrollStore } from "../_store/CustomScrollStoreModel";
import { env } from "../_utils/env";
import { useEffect } from "react";
import { bindEvent } from "../_utils/domElementEventHelpers";
import { anyWindow } from "../_utils/global";
import { useStateEx } from "../_utils/hooks";
import { callAllFns, noopFn, once } from "../_utils/fnUtils";
import { Action1, Func } from "../_utils/fnTypes";
import { IPerfectScrollState } from "../scroll/perfectScrollModel";
import { shouldBeConsumedByChild } from "../_shared/shouldBeConsumedByChild";
import { setOffsetsOnScrollAction } from "../_features/setOffsetsOnScroll/setOffsetsOnScrollActions";

const eventsGroups: any[][] = [
    ["touchstart", "touchmove", "touchend"],
    ["pointerdown", "pointermove", "pointerup"],
    ["MSPointerDown", "MSPointerMove", "MSPointerUp"],
];

const getGroupKey = once(() => {
    if (env.supportsTouch) {
        return 0;
    } else if (env.supportsIePointer) {
        if (window.PointerEvent) {
            return 1;
        } else if (anyWindow.MSPointerEvent) {
            return 2;
        }
    }
});

const defaultC = {
    startOffset: {
        pageX: 0,
        pageY: 0,
    },
    startTime: 0,
    speed: {
        x: 0,
        y: 0,
    },
    easingLoop: null,
};
type TC = typeof defaultC;

interface IArgs {
    getC: Func<TC>;
    setC: Action1<TC>;
    element: HTMLElement;
    store: ICustomScrollStore;
}

const scrollStateSelector = (s: IPerfectScrollState) => ({
    containerWidth: s.horizontalDimension?.containerMeasure,
    containerHeight: s.verticalDimension?.containerMeasure,
    contentWidth: s.horizontalDimension?.contentMeasure,
    contentHeight: s.verticalDimension?.contentMeasure,
});

export function useMouseWheelHandler(
    element: HTMLElement,
    store: ICustomScrollStore
) {
    if (!env.supportsTouch && !env.supportsIePointer) return;

    const [, setC, getC] = useStateEx(defaultC);

    useEffect(
        () =>
            element
                ? callAllFns(
                      ...[touchStart, touchMove].map((fn, i) =>
                          bindEvent(
                              element,
                              eventsGroups[getGroupKey()][i],
                              fn.bind(null, { getC, setC, element, store })
                          )
                      )
                  )
                : noopFn,
        [element]
    );
}

// touchStart
function touchStart({ getC, setC }: IArgs, e) {
    if (!shouldHandle(e)) {
        return;
    }

    const touch = getTouch(e);

    setC({
        ...defaultC,
        startOffset: {
            pageX: touch.pageX,
            pageY: touch.pageY,
        },
        startTime: new Date().getTime(),
    });

    const { easingLoop } = getC();
    if (easingLoop !== null) {
        clearInterval(easingLoop);
    }
}

function shouldHandle(e) {
    if (e.pointerType && e.pointerType === "pen" && e.buttons === 0) {
        return false;
    }
    if (e.targetTouches && e.targetTouches.length === 1) {
        return true;
    }
    if (
        e.pointerType &&
        e.pointerType !== "mouse" &&
        e.pointerType !== e.MSPOINTER_TYPE_MOUSE
    ) {
        return true;
    }
    return false;
}

function getTouch(e) {
    if (e.targetTouches) {
        return e.targetTouches[0];
    } else {
        // Maybe IE pointer
        return e;
    }
}

// \touchStart

// touchMove
function touchMove(a: IArgs, e) {
    if (!shouldHandle(e)) return;

    const c = a.getC();
    const { startOffset, speed, startTime } = c;

    const touch = getTouch(e);

    const currentOffset = { pageX: touch.pageX, pageY: touch.pageY };

    const differenceX = currentOffset.pageX - startOffset.pageX;
    const differenceY = currentOffset.pageY - startOffset.pageY;

    if (
        shouldBeConsumedByChild(a.element, e.target, differenceX, differenceY)
    ) {
        return;
    }

    applyTouchMove(a, differenceX, differenceY);
    const newC: Partial<TC> = { startOffset: currentOffset };

    const currentTime = new Date().getTime();

    const timeGap = currentTime - startTime;
    if (timeGap > 0) {
        speed.x = differenceX / timeGap;
        speed.y = differenceY / timeGap;
        newC.startTime = currentTime;
    }

    if (shouldPrevent(a, differenceX, differenceY)) {
        e.preventDefault();
    }
    a.setC({ ...c, ...newC });
}

function shouldPrevent(a: IArgs, deltaX, deltaY) {
    const scrollTop = Math.floor(a.element.scrollTop);
    const scrollLeft = a.element.scrollLeft;
    const magnitudeX = Math.abs(deltaX);
    const magnitudeY = Math.abs(deltaY);

    const i = scrollStateSelector(a.store.getState());

    if (magnitudeY > magnitudeX) {
        // user is perhaps trying to swipe up/down the page

        if (
            (deltaY < 0 && scrollTop === i.contentHeight - i.containerHeight) ||
            (deltaY > 0 && scrollTop === 0)
        ) {
            // set prevent for mobile Chrome refresh
            return window.scrollY === 0 && deltaY > 0 && env.isChrome;
        }
    } else if (magnitudeX > magnitudeY) {
        // user is perhaps trying to swipe left/right across the page

        if (
            (deltaX < 0 && scrollLeft === i.contentWidth - i.containerWidth) ||
            (deltaX > 0 && scrollLeft === 0)
        ) {
            return true;
        }
    }

    return true;
}

function applyTouchMove(
    { store }: IArgs,
    differenceX: number,
    differenceY: number
) {
    const s = store.getState();
    store.dispatch(
        setOffsetsOnScrollAction(
            s.verticalDimension.scrollMeasure - differenceY,
            s.horizontalDimension.scrollMeasure - differenceX
        )
    );
}

// \touchMove

/*
function touchEnd() {
    if (i.settings.swipeEasing) {
        clearInterval(easingLoop);
        easingLoop = setInterval(function() {
            if (i.isInitialized) {
                clearInterval(easingLoop);
                return;
            }

            if (!speed.x && !speed.y) {
                clearInterval(easingLoop);
                return;
            }

            if (Math.abs(speed.x) < 0.01 && Math.abs(speed.y) < 0.01) {
                clearInterval(easingLoop);
                return;
            }

            if (!i.element) {
                clearInterval(easingLoop);
                return;
            }

            applyTouchMove(speed.x * 30, speed.y * 30);

            speed.x *= 0.8;
            speed.y *= 0.8;
        }, 10);
    }
}*/
